import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { MarkdownECharts } from "./MarkdownECharts";
import { MarkdownMermaid } from "./MarkdownMermaid";
import { App, Collapse, Flex, Tooltip } from "antd";
import { UiButton } from "../UiButton";
import { MaterialIcon } from "../MaterialIcon";
import { useAppDispatch } from "@/app/state/AppContext";

import Style from "./index.module.css";

type MarkdownCodeProps = React.HTMLAttributes<HTMLElement> & {
  lang?: string;
  block?: boolean;
  streamStatus?: "loading" | "done";
  domNode?: unknown;
};

const CODE_COLLAPSE_CLASS_NAME = [Style.Collapse, "tw:bg-bg-elev-1"].join(" ");

function isEChartsLanguage(lang?: string): boolean {
  const language = (lang || "").trim().split(/\s+/)[0]?.toLowerCase();
  return language === "echart" || language === "echarts";
}

function isMermaidLanguage(lang?: string): boolean {
  const language = (lang || "").trim().split(/\s+/)[0]?.toLowerCase();
  return language === "mermaid" || language === "mmd" || language === "mermind";
}

function getDefaultActiveKey(language: string): string {
  return isEChartsLanguage(language) || isMermaidLanguage(language)
    ? ""
    : language;
}

function textFromReactNode(node: React.ReactNode): string {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(textFromReactNode).join("");
  }
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return textFromReactNode(node.props.children);
  }
  return "";
}

export const MarkdownCode: React.FC<MarkdownCodeProps> = ({
  lang,
  block,
  streamStatus,
  children,
  domNode: _domNode,
  ...rest
}) => {
  const { message } = App.useApp();
  const dispatch = useAppDispatch();
  const url = useRef("");
  const language = useMemo(() => lang || "plaintext", [lang]);
  const [activeKey, setActiveKey] = useState(() =>
    getDefaultActiveKey(language),
  );
  const text = useMemo(() => textFromReactNode(children), [children]);
  const onCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      navigator.clipboard.writeText(text).then(() => {
        message.success("复制成功");
      });
    },
    [text],
  );
  const extraActions = useMemo(() => {
    if (language === "html") {
      return (
        <Flex>
          <Tooltip title="复制">
            <UiButton variant="ghost" iconOnly onClick={onCopy}>
              <MaterialIcon name="content_copy" />
            </UiButton>
          </Tooltip>
          <Tooltip title="预览">
            <UiButton
              variant="ghost"
              iconOnly
              onClick={(e) => {
                e.stopPropagation();
                const mimeType = "text/html";
                const blob = new Blob([text], { type: mimeType });
                url.current && URL.revokeObjectURL(url.current);
                url.current = URL.createObjectURL(blob);
                dispatch({
                  type: "OPEN_RIGHT_SIDEBAR",
                  tab: "preview",
                  preview: {
                    name: "预览Html",
                    url: url.current,
                    downloadUrl: url.current,
                    sizeBytes: blob.size,
                    kind: "html",
                  },
                });
              }}
            >
              <MaterialIcon name="preview" />
            </UiButton>
          </Tooltip>
        </Flex>
      );
    }
    return (
      <Tooltip title="复制">
        <UiButton variant="ghost" iconOnly onClick={onCopy}>
          <MaterialIcon name="content_copy" />
        </UiButton>
      </Tooltip>
    );
  }, [dispatch, language, onCopy, text]);

  return block ? (
    <Flex vertical gap={10}>
      {isEChartsLanguage(language) && (
        <MarkdownECharts code={text} streamStatus={streamStatus} />
      )}
      {isMermaidLanguage(language) && (
        <MarkdownMermaid code={text} streamStatus={streamStatus} />
      )}
      <Collapse
        className={CODE_COLLAPSE_CLASS_NAME}
        activeKey={activeKey}
        onChange={setActiveKey as any}
        ghost
        expandIcon={({ isActive }) => (
          <MaterialIcon name="chevron_right" style={{ transform: isActive ? "rotate(90deg)" : undefined }} />
        )}
        items={[
          {
            key: language,
            label: language,
            children: <code>{children}</code>,
            extra: extraActions,
          },
        ]}
      />
    </Flex>
  ) : (
    <code {...rest}>{children}</code>
  );
};
